import { useState, useEffect, useCallback } from 'react'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'
import type { BdrLead, BdrCloser, BdrPartner, BdrStats } from '@/types'
import { OPERATIONAL_BDR_ID, emptyStats } from '@/utils/bdrPanelUtils'
import { useBdrPanelFetchBootstrap } from '@/hooks/bdrPanel/useBdrPanelFetchBootstrap'
import { useBdrPanelFetchQueries } from '@/hooks/bdrPanel/useBdrPanelFetchQueries'

export function useBdrPanelFetch(userId: string | undefined) {
  const sessionReady = useApiSessionReady()
  const currentBdrId = OPERATIONAL_BDR_ID

  const [closers, setClosers] = useState<BdrCloser[]>([])
  const [partners, setPartners] = useState<BdrPartner[]>([])

  const [leadsList, setLeadsList] = useState<BdrLead[]>([])
  const [queueLeads, setQueueLeads] = useState<BdrLead[]>([])
  const [nurturingLeads, setNurturingLeads] = useState<BdrLead[]>([])
  const [historyAgendamentos, setHistoryAgendamentos] = useState<
    {
      id: string
      empresa: string
      contato: string
      dataEntrada?: string
      dataAgendada?: string
      score: number
      status: string
      reuniaoStatus?: string
    }[]
  >([])
  const [stats, setStats] = useState<BdrStats>(emptyStats())

  const [loading, setLoading] = useState(true)
  const [isFiltering, setIsFiltering] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingNurturing, setLoadingNurturing] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)

  const [viewTab, setViewTab] = useState('queue')

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [totalLeads, setTotalLeads] = useState(0)
  const [nurturingPage, setNurturingPage] = useState(1)
  const [nurturingHasMore, setNurturingHasMore] = useState(false)
  const [nurturingTotal, setNurturingTotal] = useState(0)

  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterTemp, setFilterTemp] = useState('')
  const [filterFase, setFilterFase] = useState('')
  const [sortField, setSortField] = useState('score')
  const [sortDir, setSortDir] = useState('desc')
  const [nurturingSearch, setNurturingSearch] = useState('')

  const [bankLeads, setBankLeads] = useState<BdrLead[]>([])
  const [bankSearch, setBankSearch] = useState('')
  const [bankFilterSegmento, setBankFilterSegmento] = useState('')
  const [bankFilterPlataforma, setBankFilterPlataforma] = useState('')
  const [bankFilterOrigem, setBankFilterOrigem] = useState('')
  const [bankPage, setBankPage] = useState(1)
  const [bankTotalPages, setBankTotalPages] = useState(1)
  const [bankSortField, setBankSortField] = useState('createdAt')
  const [bankSortDir, setBankSortDir] = useState<'asc' | 'desc'>('desc')
  const [loadingBank, setLoadingBank] = useState(false)

  useBdrPanelFetchBootstrap(userId, setClosers, setPartners, setLoading)

  const {
    fetchAllLeads,
    fetchTodayQueue,
    fetchNurturingLeads,
    fetchAppointmentHistory,
    fetchBankLeads,
  } = useBdrPanelFetchQueries({
    currentBdrId,
    search,
    filterStatus,
    filterTemp,
    filterFase,
    sortField,
    sortDir,
    nurturingSearch,
    bankSearch,
    bankFilterSegmento,
    bankFilterPlataforma,
    bankFilterOrigem,
    bankSortField,
    bankSortDir,
    setLeadsList,
    setQueueLeads,
    setNurturingLeads,
    setHistoryAgendamentos,
    setStats,
    setIsFiltering,
    setLoadingMore,
    setLoadingNurturing,
    setLoadingHistory,
    setLoadingBank,
    setPage,
    setHasMore,
    setTotalLeads,
    setNurturingPage,
    setNurturingHasMore,
    setNurturingTotal,
    setBankLeads,
    setBankPage,
    setBankTotalPages,
  })

  useEffect(() => {
    if (!sessionReady) return
    if (viewTab === 'queue') {
      fetchTodayQueue()
    } else if (viewTab === 'all') {
      fetchAllLeads(1)
    } else if (viewTab === 'nurturing') {
      fetchNurturingLeads(1)
    } else if (viewTab === 'history') {
      fetchAppointmentHistory()
    } else if (viewTab === 'bank') {
      fetchBankLeads(1)
    }
  }, [
    sessionReady,
    viewTab,
    currentBdrId,
    fetchTodayQueue,
    fetchAllLeads,
    fetchNurturingLeads,
    fetchAppointmentHistory,
    fetchBankLeads,
  ])

  useEffect(() => {
    if (!sessionReady) return
    if (viewTab === 'all' && currentBdrId) {
      fetchAllLeads(1)
    }
  }, [sessionReady, search, filterStatus, filterTemp, filterFase, sortField, sortDir])

  useEffect(() => {
    if (!sessionReady) return
    if (viewTab === 'nurturing' && currentBdrId) {
      fetchNurturingLeads(1)
    }
  }, [sessionReady, nurturingSearch])

  useEffect(() => {
    if (!sessionReady) return
    if (viewTab === 'bank') {
      fetchBankLeads(1)
    }
  }, [
    sessionReady,
    bankSearch,
    bankFilterSegmento,
    bankFilterPlataforma,
    bankFilterOrigem,
    bankSortField,
    bankSortDir,
  ])

  const handleLoadMore = useCallback(
    (targetPage: number) => {
      const append = targetPage > (viewTab === 'all' ? page : nurturingPage)
      if (viewTab === 'all') {
        fetchAllLeads(targetPage, append)
      } else if (viewTab === 'nurturing') {
        fetchNurturingLeads(targetPage, append)
      }
    },
    [viewTab, page, nurturingPage, fetchAllLeads, fetchNurturingLeads],
  )

  return {
    currentBdrId,
    closers,
    partners,
    leadsList,
    setLeadsList,
    queueLeads,
    setQueueLeads,
    nurturingLeads,
    setNurturingLeads,
    historyAgendamentos,
    stats,
    loading,
    isFiltering,
    loadingMore,
    loadingNurturing,
    loadingHistory,
    viewTab,
    setViewTab,
    page,
    hasMore,
    totalLeads,
    nurturingPage,
    nurturingHasMore,
    nurturingTotal,
    search,
    setSearch,
    filterStatus,
    setFilterStatus,
    filterTemp,
    setFilterTemp,
    filterFase,
    setFilterFase,
    sortField,
    setSortField,
    sortDir,
    setSortDir,
    nurturingSearch,
    setNurturingSearch,
    bankLeads,
    bankSearch,
    setBankSearch,
    bankFilterSegmento,
    setBankFilterSegmento,
    bankFilterPlataforma,
    setBankFilterPlataforma,
    bankFilterOrigem,
    setBankFilterOrigem,
    bankPage,
    bankTotalPages,
    bankSortField,
    setBankSortField,
    bankSortDir,
    setBankSortDir,
    loadingBank,
    fetchAllLeads,
    fetchTodayQueue,
    fetchNurturingLeads,
    fetchAppointmentHistory,
    fetchBankLeads,
    handleLoadMore,
  }
}
